import { useState, useMemo } from 'react'
import { useBancoApus } from '../apus/useBancoApus'
import type { Apu } from '../apus/tipos'
import styles from './ModalSelectorApu.module.css'

interface ModalSelectorApuProps {
  abierto: boolean
  capituloNombre: string
  alCerrar: () => void
  alSeleccionar: (apu: Apu, cantidad: number) => void
}

export function ModalSelectorApu({ abierto, capituloNombre, alCerrar, alSeleccionar }: ModalSelectorApuProps) {
  const { apus } = useBancoApus()
  const [busqueda, setBusqueda] = useState('')
  const [categoria, setCategoria] = useState<string>('todas')
  const [cantidades, setCantidades] = useState<Record<string, number>>({})

  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  const categoriasDisponibles = useMemo(() => {
    const set = new Set<string>()
    apus.forEach((a) => {
      if (a.categoria) set.add(a.categoria)
    })
    return ['todas', ...Array.from(set)]
  }, [apus])

  const apusFiltrados = useMemo(() => {
    return apus.filter((a) => {
      const matchCat = categoria === 'todas' || a.categoria === categoria
      const q = busqueda.toLowerCase().trim()
      const matchBusqueda =
        !q ||
        a.codigo.toLowerCase().includes(q) ||
        a.nombre.toLowerCase().includes(q) ||
        (a.descripcion && a.descripcion.toLowerCase().includes(q))
      return matchCat && matchBusqueda
    })
  }, [apus, categoria, busqueda])

  const handleCantidadChange = (apuId: string, val: string) => {
    const num = parseFloat(val) || 1
    setCantidades((prev) => ({ ...prev, [apuId]: num }))
  }

  const handleAgregar = (apu: Apu) => {
    const cant = cantidades[apu.id] || 1
    alSeleccionar(apu, cant)
  }

  if (!abierto) return null

  return (
    <div className={styles.overlay} onClick={alCerrar}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.badgeTop}>BANCO DE APUS DISPONIBLES</div>
            <h3 className={styles.titulo}>Seleccionar Actividad APU</h3>
            <p className={styles.subtitulo}>
              Agregando ítems a: <strong>{capituloNombre}</strong>
            </p>
          </div>
          <button type="button" className={styles.btnCerrar} onClick={alCerrar}>
            ✕
          </button>
        </div>

        {/* Buscador y categorías */}
        <div className={styles.filtros}>
          <div className={styles.categorias}>
            {categoriasDisponibles.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`${styles.catBtn} ${categoria === cat ? styles.catActiva : ''}`}
                onClick={() => setCategoria(cat)}
              >
                {cat === 'todas' ? 'Todas las categorías' : cat}
              </button>
            ))}
          </div>
          <div className={styles.buscador}>
            <input
              type="text"
              placeholder="Buscar por código, nombre o descripción..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={styles.inputBuscar}
              autoFocus
            />
          </div>
        </div>

        {/* Lista de APUs */}
        <div className={styles.lista}>
          {apusFiltrados.length === 0 ? (
            <div className={styles.sinResultados}>No se encontraron APUs en esta categoría o búsqueda.</div>
          ) : (
            apusFiltrados.map((apu) => {
              const cantActual = cantidades[apu.id] ?? 1
              const pUnitario = apu.precio_unitario_total || apu.costo_directo || 0
              const subtotalEstimado = pUnitario * cantActual

              return (
                <div key={apu.id} className={styles.apuCard}>
                  <div className={styles.apuInfo}>
                    <div className={styles.apuHeaderLine}>
                      <span className={styles.codigoApu}>{apu.codigo}</span>
                      <span className={styles.unidadBadge}>{apu.unidad || 'UND'}</span>
                      <span className={styles.categoriaBadge}>{apu.categoria}</span>
                    </div>
                    <div className={styles.nombreApu}>{apu.nombre}</div>
                    {apu.descripcion && <p className={styles.descApu}>{apu.descripcion}</p>}
                    <div className={styles.preciosLine}>
                      <span>
                        Costo Directo: <strong>{formatearCOP(apu.costo_directo || 0)}</strong>
                      </span>
                      <span>
                        P. Venta Unitario: <strong className={styles.precioVenta}>{formatearCOP(pUnitario)}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={styles.apuAccion}>
                    <div className={styles.cantidadBox}>
                      <label>Cantidad:</label>
                      <input
                        type="number"
                        min="0.01"
                        step="1"
                        value={cantActual}
                        onChange={(e) => handleCantidadChange(apu.id, e.target.value)}
                        className={styles.inputCantidad}
                      />
                    </div>
                    <div className={styles.subtotalEstimado}>
                      Subtotal: <strong>{formatearCOP(subtotalEstimado)}</strong>
                    </div>
                    <button
                      type="button"
                      className={styles.btnAgregar}
                      onClick={() => handleAgregar(apu)}
                    >
                      + Agregar al Capítulo
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className={styles.footer}>
          <span>{apusFiltrados.length} APUs disponibles</span>
          <button type="button" className={styles.btnListo} onClick={alCerrar}>
            Listo
          </button>
        </div>
      </div>
    </div>
  )
}

